import { motion } from 'framer-motion';
import React from 'react';
import { FiClock, FiShield, FiTrendingUp, FiUsers } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const ITStaffingSection = () => {
  const features = [
    {
      icon: FiUsers,
      title: "Expert Talent Pool",
      description: "Access to pre-vetted IT professionals with proven track records"
    },
    {
      icon: FiTrendingUp,
      title: "Scalable Solutions",
      description: "Flexible staffing that grows with your business needs"
    },
    {
      icon: FiShield,
      title: "Quality Assurance",
      description: "Rigorous screening process ensures top-tier candidates"
    },
    {
      icon: FiClock,
      title: "Rapid Deployment",
      description: "Quick turnaround times to meet urgent project deadlines"
    }
  ];

  return (
    <section id="it-staffing" className="py-20 bg-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30"></div>
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-blue-100 to-transparent rounded-full -translate-y-48 translate-x-48 opacity-50"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="space-y-8"
          >
            <div>
              <motion.span
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                viewport={{ once: true }}
                className="inline-block px-4 py-2 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-4 font-accent tracking-wide"
              >
                Our Core Service
              </motion.span>
              <motion.h2
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                viewport={{ once: true }}
                className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight font-display tracking-tight"
              >
                IT Staffing
                <span className="block bg-gradient-to-r from-blue-600 to-indigo-700 bg-clip-text text-transparent">
                  Excellence
                </span>
              </motion.h2>
            </div>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              viewport={{ once: true }}
              className="text-lg text-gray-600 leading-relaxed font-body font-light tracking-wide"
            >
              Connect with exceptional IT professionals who drive innovation and deliver results. 
              Our comprehensive staffing solutions ensure you have the right talent at the right time, 
              whether you need temporary expertise or permanent team members.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 sm:grid-cols-2 gap-6"
            >
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  viewport={{ once: true }}
                  className="flex items-start space-x-3"
                >
                  <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <feature.icon className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1 font-heading tracking-wide">{feature.title}</h3>
                    <p className="text-sm text-gray-600 font-body">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>

            {/* CTA Button */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              viewport={{ once: true }}
              className="pt-2"
            >
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white px-8 py-3 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300 font-accent tracking-wide"
                >
                  Contact for IT Staffing
                </motion.button>
              </Link>
            </motion.div>
            
          </motion.div>

          {/* Right Visual */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative bg-gradient-to-br from-blue-600 to-indigo-700 rounded-3xl p-8 shadow-2xl">
              <div className="absolute inset-0 bg-white/10 rounded-3xl backdrop-blur-sm"></div>
              <div className="relative z-10">
                <div className="grid grid-cols-2 gap-6 mb-8">
                  {[
                    { label: "Developers", count: "150+" },
                    { label: "Architects", count: "25+" },
                    { label: "DevOps", count: "30+" },
                    { label: "QA Engineers", count: "40+" }
                  ].map((stat, index) => (
                    <motion.div
                      key={index}
                      initial={{ scale: 0 }}
                      whileInView={{ scale: 1 }}
                      transition={{ delay: 0.5 + index * 0.1, duration: 0.5 }}
                      viewport={{ once: true }}
                      className="text-center bg-white/20 rounded-xl p-4 backdrop-blur-sm"
                    >
                      <div className="text-2xl font-bold text-white font-display">{stat.count}</div>
                      <div className="text-blue-100 text-sm font-body tracking-wide">{stat.label}</div>
                    </motion.div>
                  ))}
                </div>
                
                <div className="space-y-4">
                  {[
                    "Full-Stack Developers",
                    "Cloud Specialists", 
                    "Data Scientists",
                    "Cybersecurity Experts"
                  ].map((role, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 + index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center space-x-3 bg-white/20 rounded-lg p-3 backdrop-blur-sm"
                    >
                      <div className="w-2 h-2 bg-blue-200 rounded-full"></div>
                      <span className="text-white font-medium font-body tracking-wide">{role}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ITStaffingSection; 